import { Component, ErrorInfo, ReactNode } from 'react';

import ErrorFallback from './ErrorFallback';

interface ErrorBoundaryProps {
  children: ReactNode;
  /**
   * When any value in this array changes, a caught error is cleared and children
   * are re-rendered. Used to auto-recover on navigation (see RouteErrorBoundary).
   */
  resetKeys?: unknown[];
  /** Optional custom fallback renderer; defaults to <ErrorFallback />. */
  fallback?: (args: { error: Error; reset: () => void }) => ReactNode;
  /** Optional hook for logging/telemetry. */
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Catches render/lifecycle errors in its subtree and shows a fallback instead of
 * unmounting the whole React root (which today produces a blank white screen).
 *
 * Does NOT catch errors in event handlers, async code, or React Query (queries
 * surface errors as state, not throws) — by design those are handled elsewhere.
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface for debugging; swap console for a real logger/telemetry sink when available.
    console.error('ErrorBoundary caught an error:', error, info.componentStack);
    this.props.onError?.(error, info);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    if (this.state.error && this.haveResetKeysChanged(prevProps.resetKeys, this.props.resetKeys)) {
      this.reset();
    }
  }

  private haveResetKeysChanged(prev?: unknown[], next?: unknown[]): boolean {
    if (prev === next) return false;
    if (!prev || !next || prev.length !== next.length) return true;
    return prev.some((value, index) => !Object.is(value, next[index]));
  }

  private reset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;
    const { children, fallback } = this.props;

    if (error) {
      if (fallback) {
        return fallback({ error, reset: this.reset });
      }
      return <ErrorFallback error={error} onReset={this.reset} />;
    }

    return children;
  }
}

export default ErrorBoundary;
